// Shown before a platform is switched off, from either of the two places that can do it:
// Deactivate on the Platforms tab (every client) and unchecking a chip on the Clients tab
// (one client). Same consequence at two scopes, so one dialog serves both — pass clientId to
// scope it.
//
// Disabling is never blocked. Nothing is deleted either: the taxonomy and the campaign rows
// stay exactly where they are, the platform stops being offered for new work, and the
// campaigns that used it become read-only until it comes back. The dialog exists so that is
// an informed choice rather than a discovery.
//
// Structure deliberately mirrors LevelDeleteDialog in PlatformsPage.jsx — same classes, same
// load-before-confirm rule. A dialog that can be dismissed before it knows the cost is worse
// than no dialog.
import { useState, useEffect } from 'react';
import { txApi } from '../txApi.js';
import { C, Err, Spinner, SectionLabel } from './_shared.jsx';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function PlatformDisableDialog({ platforms, clientId, clientName, onCancel, onConfirm }) {
  const [usages, setUsages] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Keyed on the ids, not the array: callers build the list inline, so a reference dep would
  // refetch on every render.
  const platformKey = platforms.map(p => p.id).join(',');
  useEffect(() => {
    let alive = true;
    Promise.all(platforms.map(p => txApi.platformUsage(p.id, clientId)))
      .then(d => { if (alive) setUsages(d); })
      .catch(e => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [platformKey, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalCampaigns = (usages || []).reduce((n, u) => n + u.totals.campaigns, 0);
  const totalRows = (usages || []).reduce((n, u) => n + u.totals.rows, 0);
  const inUse = totalCampaigns > 0 || (usages || []).some(u => u.totals.clients > 0);

  const names = platforms.map(p => p.name).join(', ');
  const scope = clientName ? <> for <b>{clientName}</b></> : ' for every client';

  return (
    <div className="o-scrim" onClick={onCancel}>
      <div className="o-modal" onClick={e => e.stopPropagation()}>
        <div className="o-modal-head">
          <h2>Disable {names}?</h2>
        </div>

        <div className="o-modal-body">
          {!usages && !err && <Spinner />}
          {err && <Err msg={err} />}

          {usages && !inUse && (
            <p style={{ margin: 0, fontSize: 13, color: C.dim }}>
              Nothing is configured or built on {platforms.length === 1 ? 'this platform' : 'these platforms'}
              {scope}. Disabling {platforms.length === 1 ? 'it' : 'them'} affects nothing else.
            </p>
          )}

          {usages && inUse && (
            <>
              <p style={{ margin: 0, fontSize: 13, color: C.danger, fontWeight: 600 }}>
                {totalCampaigns > 0
                  ? `${plural(totalCampaigns, 'campaign')} holding ${plural(totalRows, 'row')} become read-only, and no new taxonomy or campaigns can be created${clientName ? ` for ${clientName}` : ''} on ${platforms.length === 1 ? 'this platform' : 'these platforms'}.`
                  : `No campaign has been built yet, but the taxonomy below stops being reachable and no new campaigns can be created against ${platforms.length === 1 ? 'it' : 'them'}.`}
              </p>

              {usages.map((u, ui) => (
                (u.clients.length > 0 || u.campaigns.length > 0) && (
                  <section key={u.platform.id}>
                    <SectionLabel style={{ marginBottom: 6 }}>
                      {platforms.length === 1 ? 'Configured on this platform' : u.platform.name}
                    </SectionLabel>

                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ textAlign: 'left', color: C.muted }}>
                          <th style={{ padding: '6px 8px' }}>Client</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Levels</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Versions</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Fields</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Rows</th>
                        </tr>
                      </thead>
                      <tbody>
                        {u.clients.map(c => (
                          <tr key={c.client_id} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                            <td style={{ padding: '6px 8px' }}>{c.client_name}</td>
                            {[c.level_count, c.version_count, c.field_count, c.row_count].map((n, i) => (
                              <td key={i} style={{
                                padding: '6px 8px', textAlign: 'right', fontFamily: C.mono,
                                color: n ? C.text : C.muted,
                              }}>{n}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {u.campaigns.length > 0 && (
                      <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 12, color: C.dim }}>
                        {u.campaigns.map((c, i) => (
                          <li key={i} style={{ marginBottom: 2 }}>
                            <b style={{ color: C.text }}>{c.campaign_name}</b>
                            <span style={{ color: C.muted }}> · {c.client_name} · </span>
                            <span style={{ fontFamily: C.mono }}>{c.row_count}</span>
                            <span style={{ color: C.muted }}> row{c.row_count === 1 ? '' : 's'}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {ui < usages.length - 1 && <div style={{ height: 4 }} />}
                  </section>
                )
              ))}

              <p style={{ margin: 0, fontSize: 12, color: C.muted }}>
                Nothing is deleted. Those campaigns keep every row and stay visible — they just
                cannot be edited.{' '}
                {clientName
                  ? `Re-assigning the platform to ${clientName} makes them editable again.`
                  : 'Reactivating the platform makes them editable again.'}
              </p>
            </>
          )}
        </div>

        <div className="o-modal-foot">
          <button className="o-btn" onClick={onCancel}>Cancel</button>
          <button
            className={inUse ? 'o-btn o-btn-danger' : 'o-btn o-btn-primary'}
            disabled={!usages || busy}
            onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } }}
          >
            {busy ? 'Disabling…'
              : !inUse ? 'Disable'
              : totalCampaigns > 0 ? `Disable anyway — ${plural(totalCampaigns, 'campaign')} affected`
              : 'Disable anyway'}
          </button>
        </div>
      </div>
    </div>
  );
}
